import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useToast } from '@welbe/web-design-system';
import { loggerInfo } from 'utils/infoLog';
import { HealthRiskAssessmentContext } from 'Views/HealthRiskAssessment/HealthRiskAssessment.context';
import { ParamsProps } from '../../Population.types';
import {
	defaultOptions,
	defaultTabs,
	multipleOption,
} from './DrawerEmployees.constants';
import {
	getAmountAllResults,
	getAmountReceivedResults,
} from './DrawerEmployees.functions';
import {
	getEmployeesWithActions,
	putEmployeesWithActions,
} from './DrawerEmployees.services';
import {
	ActionsActivityType,
	ActivityType,
	DrawerEmployeesContextProps,
	DrawerEmployeesProviderProps,
	TabType,
} from './DrawerEmployees.types';

export const DrawerEmployeesContext = createContext(
	{} as DrawerEmployeesContextProps,
);

export const DrawerEmployeesProvider = ({
	employees,
	onClose,
	children,
}: DrawerEmployeesProviderProps): JSX.Element => {
	const { toast } = useToast();
	const { companyId } = useParams() as ParamsProps;
	const { selectedGroup } = useContext(HealthRiskAssessmentContext);
	const [active, setActive] = useState<number>(0);
	const [isLoading, setLoading] = useState<boolean>(false);
	const [actionsActivity, setActionsActivity] = useState<ActionsActivityType>();
	const [originalActionsActivity, setOriginalActionsActivity] =
		useState<ActionsActivityType>();
	const [tabs, setTabs] = useState<TabType[]>([]);
	const [isSaving, setSaving] = useState<boolean>(false);
	const isMultiple = employees.length > 1;

	const employeesList = employees.map(({ id, name }) => ({
		label: name,
		value: id,
	}));

	const options = isMultiple
		? [...defaultOptions, multipleOption]
		: defaultOptions;

	const hasAllResultsReceived = (actionId: string) => {
		if (!selectedFormData) return false;

		const item = selectedFormData.find((item) => item.actionId === actionId);
		const receivedResults = getAmountReceivedResults(item?.orders);
		const allResults = getAmountAllResults(item?.orders);
		const isFinished = allResults > 0 && receivedResults === allResults;

		return isFinished;
	};

	const findChangedActivities = (
		original: ActivityType[],
		current: ActivityType[],
	) => {
		return current.filter((currentItem) => {
			const originalItem = original.find(
				(originalItem) => originalItem.actionId === currentItem.actionId,
			);
			return originalItem?.status !== currentItem.status;
		});
	};

	const getOnlyUpdatedActivities = () => {
		const currentActivities = actionsActivity?.activities || {};
		const originalActivities = originalActionsActivity?.activities || {};

		return Object.keys(currentActivities).reduce(
			(updatedActivities, activityKey) => {
				const originalActivitiesForKey = originalActivities[activityKey] || [];
				const currentActivitiesForKey = currentActivities[activityKey] || [];
				const changedActivities = findChangedActivities(
					originalActivitiesForKey,
					currentActivitiesForKey,
				);

				if (changedActivities.length > 0)
					updatedActivities[activityKey] = changedActivities;

				return updatedActivities;
			},
			{} as Record<string, ActivityType[]>,
		);
	};

	const handleChangeData = (actionId: string, key: string, value: string) => {
		if (!actionsActivity || !actionsActivity.activities) return;

		if (key === 'exams') {
			const isFinished = hasAllResultsReceived(actionId);

			if (!isFinished) return;
		}

		const updatedData = actionsActivity.activities[key].map((item) =>
			item.actionId === actionId ? { ...item, status: value } : { ...item },
		);

		const updatedTabs = tabs.map((tab) =>
			tab.key === key ? { ...tab, data: updatedData } : { ...tab },
		);

		setTabs(updatedTabs);
		setActionsActivity({
			activities: { ...actionsActivity.activities, [key]: updatedData },
		});
	};

	const fetchDrawerData = async () => {
		setLoading(true);
		try {
			const employeeIds =
				employees.map((employee) => employee.employeeId)?.join(',') || '';

			const { data } = await getEmployeesWithActions(
				companyId,
				selectedGroup?.hraCompanyGroupId || '',
				employeeIds,
			);

			const keysToFilter = Object.keys(data.content.activities);
			const filteredTabs = defaultTabs
				.filter((tab) => keysToFilter.includes(tab.key))
				.map((tab, index) => ({
					...tab,
					id: index,
				}));

			setTabs(filteredTabs);
			setActionsActivity(data.content);
			setOriginalActionsActivity(data.content);
		} catch (error) {
			loggerInfo({
				data: error,
				context: 'hra',
				location: 'GET on HRA Population Drawer',
			});
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const handleSave = async () => {
		setSaving(true);

		try {
			const employeeIds = employees.map((employee) => employee.employeeId);
			const changedActivities = getOnlyUpdatedActivities();

			const formattedActions = {
				employeeIds,
				activities: Object.values(changedActivities || {})
					.flat()
					.map(({ actionId, status }) => ({
						actionId,
						status,
					})),
			};

			const { data } = await putEmployeesWithActions(
				companyId,
				selectedGroup?.hraCompanyGroupId || '',
				formattedActions,
			);

			toast.success(
				`${data.content.updatedCount} estatus de actividad guardados correctamente`,
			);
			onClose(true);
		} catch (error) {
			loggerInfo({
				data: error,
				context: 'hra',
				location: 'PUT on HRA Population Drawer',
			});
			toast.error('Ha ocurrido un error al guardar los cambios');
			console.error(error);
		} finally {
			setSaving(false);
		}
	};

	const selectedFormData = useMemo(() => {
		if (!tabs) return;

		const key = tabs?.[active]?.key;
		return actionsActivity?.activities?.[key];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [active, tabs]);

	useEffect(() => {
		fetchDrawerData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<DrawerEmployeesContext.Provider
			value={{
				handleSave,
				handleChangeData,
				employeesList,
				active,
				setActive,
				isLoading,
				tabs,
				onClose,
				options,
				actionsActivity,
				selectedFormData,
				isSaving,
				isMultiple,
			}}
		>
			{children}
		</DrawerEmployeesContext.Provider>
	);
};
